/**
 * WiFi Portal Settings Tab
 * Admin interface for managing guest WiFi welcome page and signup tracking
 */
import { useState, useEffect } from 'react';
import { 
  Wifi, Settings, Users, Mail, MessageSquare, Download, 
  ExternalLink, TrendingUp, UserPlus, RefreshCw, Check,
  Calendar, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function WifiSettingsTab() {
  const [settings, setSettings] = useState({
    headline: 'Welcome to Bulaccino Free Wi-Fi',
    message: 'Join Bulaccino Rewards, order online, and receive cafe updates.',
    redirect_destination: 'order',
    name_required: true,
    email_required: true,
    phone_required: true,
    email_consent_text: 'Yes, send me special offers and updates via email',
    sms_consent_text: 'Yes, send me exclusive deals via SMS',
    is_active: true
  });
  const [stats, setStats] = useState(null);
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeView, setActiveView] = useState('settings'); // 'settings' | 'signups' | 'stats'

  // Fetch settings and stats
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, statsRes, signupsRes] = await Promise.all([
        fetch(`${API_URL}/api/wifi/settings`),
        fetch(`${API_URL}/api/wifi/signups/stats`),
        fetch(`${API_URL}/api/wifi/signups?limit=20`)
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (signupsRes.ok) {
        const data = await signupsRes.json();
        setSignups(data.signups || []);
      }
    } catch (err) {
      console.error('Failed to fetch WiFi data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/wifi/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success('WiFi settings saved!');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (err) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    window.open(`${API_URL}/api/wifi/signups/export`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-xl flex items-center justify-center">
            <Wifi className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">WiFi Portal</h1>
            <p className="text-sm text-muted-foreground">Manage guest WiFi welcome page & customer capture</p>
          </div>
        </div>
        <a
          href="/wifi"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Preview Page
        </a>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <UserPlus className="w-4 h-4" />
              <span className="text-sm">Today</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats.today || 0}</p>
          </div>
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-sm">This Week</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats.this_week || 0}</p>
          </div>
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">This Month</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats.this_month || 0}</p>
          </div>
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">Total</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats.total || 0}</p>
          </div>
        </div>
      )}

      {/* Consent Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400 mb-1">
              <Mail className="w-4 h-4" />
              <span className="text-sm">Email Opt-In</span>
            </div>
            <p className="text-2xl font-bold text-green-700 dark:text-green-400">{stats.email_opt_in_rate || 0}%</p>
            <p className="text-xs text-green-600 dark:text-green-500">{stats.email_opted_in || 0} subscribers</p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400 mb-1">
              <MessageSquare className="w-4 h-4" />
              <span className="text-sm">SMS Opt-In</span>
            </div>
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{stats.sms_opt_in_rate || 0}%</p>
            <p className="text-xs text-blue-600 dark:text-blue-500">{stats.sms_opted_in || 0} subscribers</p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-purple-700 dark:text-purple-400 mb-1">
              <UserPlus className="w-4 h-4" />
              <span className="text-sm">New Customers</span>
            </div>
            <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{stats.new_customers || 0}</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">Returning</span>
            </div>
            <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">{stats.returning_customers || 0}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <button
          onClick={() => setActiveView('settings')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeView === 'settings' 
              ? 'bg-primary text-primary-foreground' 
              : 'text-muted-foreground hover:bg-muted'
          }`}
        >
          <Settings className="w-4 h-4 inline mr-2" />
          Settings
        </button>
        <button
          onClick={() => setActiveView('signups')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeView === 'signups' 
              ? 'bg-primary text-primary-foreground' 
              : 'text-muted-foreground hover:bg-muted'
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Recent Signups
        </button>
      </div>

      {/* Settings View */}
      {activeView === 'settings' && (
        <div className="bg-card border rounded-xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Welcome Page Settings</h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm text-muted-foreground">Active</span>
              <input
                type="checkbox"
                checked={settings.is_active}
                onChange={(e) => setSettings({ ...settings, is_active: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
              />
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Headline</Label>
              <Input
                value={settings.headline}
                onChange={(e) => setSettings({ ...settings, headline: e.target.value })}
                placeholder="Welcome to Bulaccino Free Wi-Fi"
              />
            </div>
            <div>
              <Label>Redirect After Signup</Label>
              <select
                value={settings.redirect_destination}
                onChange={(e) => setSettings({ ...settings, redirect_destination: e.target.value })}
                className="w-full h-10 px-3 border rounded-lg bg-background"
              >
                <option value="order">Order Online Page</option>
                <option value="rewards">Rewards Dashboard</option>
                <option value="website">Main Website</option>
              </select>
            </div>
          </div>

          <div>
            <Label>Welcome Message</Label>
            <textarea
              value={settings.message}
              onChange={(e) => setSettings({ ...settings, message: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg bg-background resize-none"
              placeholder="Join Bulaccino Rewards, order online, and receive cafe updates."
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Email Consent Text</Label>
              <Input
                value={settings.email_consent_text}
                onChange={(e) => setSettings({ ...settings, email_consent_text: e.target.value })}
                placeholder="Yes, send me special offers via email"
              />
            </div>
            <div>
              <Label>SMS Consent Text</Label>
              <Input
                value={settings.sms_consent_text}
                onChange={(e) => setSettings({ ...settings, sms_consent_text: e.target.value })}
                placeholder="Yes, send me exclusive deals via SMS"
              />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.name_required}
                onChange={(e) => setSettings({ ...settings, name_required: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="text-sm">Name Required</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.email_required}
                onChange={(e) => setSettings({ ...settings, email_required: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="text-sm">Email Required</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.phone_required}
                onChange={(e) => setSettings({ ...settings, phone_required: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="text-sm">Phone Required</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </div>
      )}

      {/* Signups View */}
      {activeView === 'signups' && (
        <div className="bg-card border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold text-foreground">Recent WiFi Signups</h2>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Phone</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Email Opt-In</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase">SMS Opt-In</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {signups.map((signup, idx) => (
                  <tr key={signup.signup_id || idx} className="hover:bg-muted/50">
                    <td className="px-4 py-3 text-sm text-foreground">
                      {new Date(signup.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{signup.name}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{signup.email}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{signup.phone}</td>
                    <td className="px-4 py-3 text-center">
                      {signup.email_marketing_consent ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          <Check className="w-3 h-3 mr-1" />
                          Yes
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {signup.sms_marketing_consent ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                          <Check className="w-3 h-3 mr-1" />
                          Yes
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {signup.is_new_customer ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                          New
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                          Returning
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {signups.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      No WiFi signups yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Setup Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">TP-Link Router Setup</h3>
        <p className="text-sm text-blue-700 dark:text-blue-400 mb-2">
          Configure your TP-Link guest WiFi captive portal to redirect to:
        </p>
        <code className="block bg-blue-100 dark:bg-blue-900/40 px-3 py-2 rounded text-sm text-blue-900 dark:text-blue-200">
          https://bulaccinocafe.com/wifi
        </code>
        <p className="text-xs text-blue-600 dark:text-blue-500 mt-2">
          This URL works in captive portal browsers on iPhone, Android, and all major browsers.
        </p>
      </div>
    </div>
  );
}
